import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  menuItemRequests,
  type CreateMenuInput,
  type CreateMenuItemInput,
  type UpdateMenuItemInput,
} from '@/lib/api/requests/menu-items';
import { menuItemKeys } from './keys';
import { recipeKeys } from '../recipes/keys';
import type { Menu } from '@/types/menu';
import type { PaginatedResponse } from '@/types/common';

export function useCreateMenuItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateMenuItemInput) => menuItemRequests.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: menuItemKeys.lists() });
      // Which plated recipes are taken has changed (the form's picker shows it).
      queryClient.invalidateQueries({ queryKey: recipeKeys.lists() });
    },
  });
}

export function useUpdateMenuItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateMenuItemInput }) =>
      menuItemRequests.update(id, data),
    onSuccess: (result, { id }) => {
      queryClient.setQueryData(menuItemKeys.detail(id), result);
      queryClient.invalidateQueries({ queryKey: menuItemKeys.lists() });
      queryClient.invalidateQueries({ queryKey: recipeKeys.lists() });
    },
  });
}

export function useCreateMenu() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateMenuInput) => menuItemRequests.createMenu(data),
    onSuccess: (menu, variables) => {
      // The caller creating a menu inline is about to select it, so put it on
      // the list straight away instead of leaving the form pointing at a menu
      // that has no option until the refetch lands (T-903). The invalidation
      // below still replaces this with the server's answer.
      queryClient.setQueryData<PaginatedResponse<Menu>>(
        [...menuItemKeys.menus(), variables.companyId ?? null],
        (previous) =>
          previous && !previous.data.some((m) => m.id === menu.id)
            ? {
                ...previous,
                data: [...previous.data, menu],
                meta: { ...previous.meta, total: previous.meta.total + 1 },
              }
            : previous,
      );
      queryClient.invalidateQueries({ queryKey: menuItemKeys.menus() });
    },
  });
}

export function useDeleteMenuItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => menuItemRequests.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: menuItemKeys.lists() });
      queryClient.invalidateQueries({ queryKey: recipeKeys.lists() });
    },
  });
}
